import { Badge } from '../components/ui/badge';
import { Mail, MailOpen, CornerDownLeft, Eye, MousePointerClick } from 'lucide-react';
import { Label } from '../components/ui/label';

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const EmailHistory = ({ emails }) => {
  if (!emails || emails.length === 0) {
    return (
      <div className="border-t border-slate-800 pt-4" data-testid="email-history">
        <Label className="text-slate-300 mb-2 block flex items-center gap-2"><Mail className="w-4 h-4" /> Email History</Label>
        <p className="text-xs text-slate-600 italic">No emails sent or received yet.</p>
      </div>
    );
  }
  return (
    <div className="border-t border-slate-800 pt-4" data-testid="email-history">
      <Label className="text-slate-300 mb-2 block flex items-center gap-2"><Mail className="w-4 h-4" /> Email History ({emails.length})</Label>
      <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
        {emails.map((e, i) => {
          const inbound = e.direction === 'in';
          return (
            <div
              key={e.id || i}
              className={`rounded-md p-3 border ${inbound ? 'bg-emerald-950/20 border-emerald-900/40' : 'bg-slate-950 border-slate-800'}`}
              data-testid={`email-${e.type}-${i}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  {inbound
                    ? <CornerDownLeft className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    : (e.opened ? <MailOpen className="w-3.5 h-3.5 text-violet-400 shrink-0" /> : <Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" />)}
                  <span className="text-sm text-white truncate">{e.subject || '(no subject)'}</span>
                </div>
                <span className="text-[11px] text-slate-600 whitespace-nowrap">{fmt(e.at)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                <Badge variant="outline" className={`text-[10px] ${inbound ? 'border-emerald-700/50 text-emerald-400' : 'border-violet-700/50 text-violet-400'}`}>
                  {inbound ? 'Received' : 'Sent'}
                </Badge>
                {e.source_label && <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400" data-testid={`email-source-${i}`}>{e.source_label}</Badge>}
                {e.failed && <Badge variant="outline" className="text-[10px] border-red-700/50 text-red-400">Failed</Badge>}
                {e.is_followup && <Badge variant="outline" className="text-[10px] border-amber-700/50 text-amber-400">Follow-up</Badge>}
                {!inbound && e.opened && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><Eye className="w-3 h-3" /> Opened</span>}
                {!inbound && e.clicked && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><MousePointerClick className="w-3 h-3" /> Clicked</span>}
                {!inbound && e.replied && <Badge variant="outline" className="text-[10px] border-emerald-700/50 text-emerald-400">Replied</Badge>}
                {inbound && e.sentiment && <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400 capitalize">{e.sentiment}</Badge>}
                {(e.to || e.from) && <span className="text-[10px] text-slate-600 truncate">{inbound ? `from ${e.from}` : `to ${e.to}`}</span>}
              </div>
              {e.body && (
                <p className="text-xs text-slate-400 line-clamp-3 whitespace-pre-wrap">{e.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 280)}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmailHistory;
